import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingCart, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import logo from "../assets/TACOLOGO4.png";

// ✅ Local Images
import lightingImg from "../assets/tacoma1.jpg";
import suspensionKitsImg from "../assets/tacoma4.jpg";
import shocksImg from "../assets/tacoma5.jpg";
import roofRacksImg from "../assets/TACO3.png";
import bedRacksImg from "../assets/tacoma1.jpg";
import campingGearImg from "../assets/instalation.png";
import shopBannerImg from "../assets/tacoma4.jpg";
import topSellersImg from "../assets/tacoma5.jpg";
import tgMadeImg from "../assets/tacoma33.webp";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isShopSubmenuOpen, setIsShopSubmenuOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const location = useLocation();
  
  // ✅ Use CartContext for cart state
  const { count: cartCount } = useCart();

  // ✅ Scroll reset — only on desktop
  useEffect(() => {
    if (window.innerWidth >= 1024) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ Cart count is now managed by global CartContext

  // Mega Menu Data - Aligned with shop page categories
  const megaMenuData = useMemo(
    () => ({
      shop: {
        title: "Shop",
        groups: [
          {
            title: "Featured",
            items: [
              { title: "Top Sellers", href: "/shop?category=top-sellers", image: topSellersImg },
              { title: "TGMade", href: "/shop?category=tgmade", image: tgMadeImg },
            ],
          },
          {
            title: "Categories",
            items: [
              { title: "Lighting", href: "/shop?category=lighting", image: lightingImg },
              { title: "Suspension", href: "/shop?category=suspension", image: suspensionKitsImg },
              { title: "Armor", href: "/shop?category=armor", image: shocksImg },
              { title: "Accessories", href: "/shop?category=accessories", image: roofRacksImg },
              { title: "Mounts", href: "/shop?category=mounts", image: bedRacksImg },
              { title: "Apparel", href: "/shop?category=apparel", image: campingGearImg },
            ],
          },
        ],
      },
    }),
    []
  );

  const navItems = [
    { name: "Home", to: "/" },
    { name: "Shop", to: "/shop", hasDropdown: true, dropdownKey: "shop" },
    { name: "About", to: "/about" },
    { name: "Contact", to: "/contact" },
  ];

  const refreshToHome = () => (window.location.href = "/");

  // --- Helper to delay dropdown close ---
  const handleMouseEnter = (dropdownKey) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    if (dropdownKey) setActiveDropdown(dropdownKey);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 10);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${
        isScrolled ? "bg-charcoal shadow-lg border-b border-darkgray" : "bg-charcoal"
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* ✅ Logo */}
          <button onClick={refreshToHome} className="flex items-center">
            <img src={logo} alt="Taco Garage Logo" className="h-12 w-auto object-contain" />
          </button>

          {/* ✅ Desktop Nav */}
          <nav className="hidden lg:flex space-x-8">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative flex items-center"
                onMouseEnter={() => handleMouseEnter(item.hasDropdown ? item.dropdownKey : null)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={item.to}
                  className="flex items-center gap-1 text-offwhite hover:text-olive transition-colors duration-300 font-medium py-2"
                >
                  {item.name}
                  {item.hasDropdown && (
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${
                        activeDropdown === item.dropdownKey ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>

                {/* ✅ Desktop Mega Menu */}
                {item.hasDropdown && (
                  <AnimatePresence>
                    {activeDropdown === item.dropdownKey && (
                      <>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.5 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                          className="fixed inset-0 bg-black/60 z-[800] hidden lg:block pointer-events-none"
                        />

                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.15 }}
                          className="fixed top-16 left-0 right-0 mx-auto w-[90vw] max-w-5xl 
                                     bg-[#121212]/95 backdrop-blur-sm border border-gray-700 
                                     rounded-xl shadow-2xl shadow-black/40 overflow-hidden hidden lg:block z-[9999]"
                          role="menu"
                          onMouseEnter={() => handleMouseEnter(item.dropdownKey)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {/* Shop All Products Banner */}
                          <Link
                            to="/shop"
                            onClick={() => setActiveDropdown(null)}
                            className="block relative w-full h-40 md:h-48 overflow-hidden group"
                          >
                            <img
                              src={shopBannerImg}
                              alt="Shop All Products"
                              className="object-cover w-full h-full opacity-80 group-hover:opacity-100 transition-opacity duration-200"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
                            <span className="absolute inset-0 flex items-center justify-center text-white text-xl md:text-2xl font-bold drop-shadow-lg">
                              Shop All Products →
                            </span>
                          </Link>

                          {/* Category Grid with Centered Thumbnails */}
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-6 md:gap-y-8 gap-x-6 p-6 sm:p-8">
                            {megaMenuData[item.dropdownKey].groups.flatMap((group) => 
                              group.items.map((menuItem, index) => (
                                <Link
                                  key={`${group.title}-${index}`}
                                  to={menuItem.href}
                                  onClick={() => setActiveDropdown(null)}
                                  className="flex flex-col items-center text-center group"
                                >
                                  <img
                                    src={menuItem.image}
                                    alt={menuItem.title}
                                    className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover shadow-md group-hover:scale-105 transition-transform duration-200"
                                  />
                                  <span className="mt-3 text-gray-200 group-hover:text-white text-sm md:text-base font-medium">
                                    {menuItem.title}
                                  </span>
                                </Link>
                              ))
                            )}
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </nav>

          {/* ✅ Desktop Right Icons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link to="/cart" className="relative p-2 text-offwhite hover:text-olive transition">
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <motion.span 
                  key={cartCount}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className="absolute -top-1 -right-1 bg-brandred text-white text-xs font-bold rounded-full px-1.5 min-w-[20px] h-5 flex items-center justify-center"
                >
                  {cartCount}
                </motion.span>
              )}
            </Link>

            <Link
              to="/shop"
              className="px-6 py-2 rounded-lg bg-brandred text-offwhite font-bold shadow-lg hover:bg-gradient-to-r hover:from-brandred hover:to-[#E63B3B] transition-all"
            >
              Shop Now
            </Link>
          </div>

          {/* ✅ Mobile Hamburger */}
          <div className="lg:hidden">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-offwhite hover:text-olive transition"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* ✅ Mobile Drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="absolute top-16 left-0 w-full h-[calc(100vh-4rem)] overflow-y-auto bg-white/95 backdrop-blur-md text-gray-900 shadow-lg z-40 p-6 space-y-4 rounded-b-3xl lg:hidden"
          >
            {/* Mobile Cart Link */}
            <Link
              to="/cart"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-between w-full text-lg font-semibold py-2 border-b border-gray-200"
            >
              <span className="flex items-center gap-2">
                <ShoppingCart size={20} />
                Cart
              </span>
              {cartCount > 0 && (
                <motion.span 
                  key={cartCount}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-brandred text-white text-xs font-bold rounded-full px-2 py-1 min-w-[20px] h-5 flex items-center justify-center"
                >
                  {cartCount}
                </motion.span>
              )}
            </Link>
            
            {navItems.map((item) => (
              <div key={item.name}>
                <button
                  onClick={() => {
                    if (item.hasDropdown) setIsShopSubmenuOpen(!isShopSubmenuOpen);
                    else {
                      setIsMenuOpen(false);
                      window.location.href = item.to;
                    }
                  }}
                  className="w-full flex justify-between items-center text-lg font-semibold py-2"
                >
                  {item.name}
                  {item.hasDropdown && (
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${isShopSubmenuOpen ? "rotate-180" : ""}`}
                    />
                  )}
                </button>

                {item.hasDropdown && isShopSubmenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    transition={{ type: "spring", stiffness: 120, damping: 16 }}
                    className="mt-3 bg-gray-50 rounded-xl shadow-lg border border-gray-200 p-4"
                  >
                    {/* Unified Grid - All Items */}
                    <div className="grid grid-cols-1 gap-2">
                      {megaMenuData.shop.groups.flatMap((group) =>
                        group.items.map((sub, j) => (
                          <motion.button
                            key={`${group.title}-${j}`}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => {
                              setIsMenuOpen(false);
                              window.location.href = sub.href;
                            }}
                            className="flex items-center gap-3 w-full text-gray-800 hover:text-brandred hover:bg-gray-200/70 px-3 py-2 rounded-md text-sm transition-all duration-150"
                          >
                            <img
                              src={sub.image}
                              alt={sub.title}
                              className="w-12 h-12 object-cover rounded-md shadow-sm flex-shrink-0"
                            />
                            <span className="font-medium text-left">{sub.title}</span>
                          </motion.button>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
